import { useEffect, useMemo, useRef, useState } from "react";
import {
  Trophy,
  RotateCcw,
  Lightbulb,
  Eraser,
  CheckCircle2,
  Boxes,
  CalendarDays,
  Flame,
  Share2,
  Lock,
  Undo2,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  findConflicts,
  generatePuzzle,
  isSolved,
  type ChemDokuPack,
  type ChemDokuPuzzle,
  type ChemDokuSize,
  type Difficulty,
} from "@/games/chemDoku/engine";
import { PACKS } from "@/games/chemDoku/packs";
import {
  buildShareText,
  getDailyPuzzle,
  loadProgress,
  loadResult,
  loadStreak,
  saveProgress,
  saveResult,
  todayKey,
  type DailyConfig,
  type DailyResult,
} from "@/games/chemDoku/daily";
import { useToast } from "@/hooks/use-toast";

/**
 * Chem-doku - Sudoku played with element symbols.
 *
 * Flow:
 *  1. Pick size (4x4 / 6x6), a symbol pack, and difficulty.
 *  2. Tap a cell to select it, then tap a symbol below the grid to fill
 *     it. Tap a filled (unlocked) cell + the same symbol to clear it,
 *     or use the Eraser button.
 *  3. Conflicts highlight in red as you go.
 *  4. "Check" verifies the full board against the unique solution.
 *  5. Win dialog offers a fresh puzzle.
 */

const SIZE_OPTIONS: ChemDokuSize[] = [4, 6];
const DIFFICULTIES: Difficulty[] = ["easy", "medium", "hard"];
const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
};

/**
 * Pastel color palette per symbol. Picks a slot deterministically by
 * hashing the symbol so each element keeps the same color across the
 * grid and palette (and across renders).
 */
// Tuned for AA contrast in both modes:
//   light: solid pastel bg + near-black tinted text
//   dark : muted tinted bg (~35% alpha) + bright tinted text
const SYMBOL_PALETTE: Array<{ bg: string; text: string; hoverRing: string }> = [
  { bg: "bg-yellow-200 dark:bg-yellow-400/25",  text: "text-yellow-950 dark:text-yellow-100",  hoverRing: "hover:ring-yellow-400/70" },
  { bg: "bg-emerald-200 dark:bg-emerald-400/25", text: "text-emerald-950 dark:text-emerald-100", hoverRing: "hover:ring-emerald-400/70" },
  { bg: "bg-rose-200 dark:bg-rose-400/25",      text: "text-rose-950 dark:text-rose-100",      hoverRing: "hover:ring-rose-400/70" },
  { bg: "bg-violet-200 dark:bg-violet-400/25",  text: "text-violet-950 dark:text-violet-100",  hoverRing: "hover:ring-violet-400/70" },
  { bg: "bg-sky-200 dark:bg-sky-400/25",        text: "text-sky-950 dark:text-sky-100",        hoverRing: "hover:ring-sky-400/70" },
  { bg: "bg-orange-200 dark:bg-orange-400/25",  text: "text-orange-950 dark:text-orange-100",  hoverRing: "hover:ring-orange-400/70" },
];

function symbolColor(symbol: string) {
  let h = 0;
  for (let i = 0; i < symbol.length; i++) h = (h * 31 + symbol.charCodeAt(i)) >>> 0;
  return SYMBOL_PALETTE[h % SYMBOL_PALETTE.length];
}

type Mode = "free" | "daily";

function deepCopy(b: (string | null)[][]): (string | null)[][] {
  return b.map((row) => row.slice());
}

/** A snapshot of board + counters captured before any reversible action. */
type HistoryEntry = {
  board: (string | null)[][];
  moves: number;
  hintsUsed: number;
};

export default function ChemDoku() {
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("free");

  const [size, setSize] = useState<ChemDokuSize>(4);
  const [packId, setPackId] = useState<string>(PACKS[4][0].id);
  const [difficulty, setDifficulty] = useState<Difficulty>("easy");

  const pack: ChemDokuPack = useMemo(
    () => PACKS[size].find((p) => p.id === packId) ?? PACKS[size][0],
    [size, packId],
  );

  // Reset pack selection when size changes (4-packs aren't valid for 6-grid).
  useEffect(() => {
    if (!PACKS[size].some((p) => p.id === packId)) {
      setPackId(PACKS[size][0].id);
    }
  }, [size, packId]);

  const [puzzle, setPuzzle] = useState<ChemDokuPuzzle | null>(null);
  const [board, setBoard] = useState<(string | null)[][]>([]);
  const [selected, setSelected] = useState<{ r: number; c: number } | null>(null);
  const [checkResult, setCheckResult] =
    useState<null | "win" | "incomplete" | "wrong">(null);
  const [winOpen, setWinOpen] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [moves, setMoves] = useState(0);
  // Undo stack: snapshots taken before each placement / hint.
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  // ---------- daily mode state ----------
  const [dailyConfig, setDailyConfig] = useState<DailyConfig | null>(null);
  const [dailyResult, setDailyResult] = useState<DailyResult | null>(null);
  const [streak, setStreak] = useState<{ current: number; best: number }>({
    current: 0,
    best: 0,
  });
  // Wall-clock start time so we can record solve duration for share text.
  const dailyStartRef = useRef<number | null>(null);

  function startPuzzle(
    s: ChemDokuSize = size,
    p: ChemDokuPack = pack,
    d: Difficulty = difficulty,
  ) {
    const next = generatePuzzle({ size: s, pack: p, difficulty: d });
    setPuzzle(next);
    setBoard(deepCopy(next.given));
    setSelected(null);
    setCheckResult(null);
    setHintsUsed(0);
    setMoves(0);
    setWinOpen(false);
    setHistory([]);
  }

  /**
   * Load (or generate) the puzzle for the current mode. In daily mode we
   * derive everything from today's date and restore any saved progress
   * so the player can resume mid-puzzle. In free mode we honour the
   * size/pack/difficulty pickers.
   */
  useEffect(() => {
    if (mode === "daily") {
      const { config, puzzle: dp } = getDailyPuzzle();
      setDailyConfig(config);
      setPuzzle(dp);

      const saved = loadProgress(config.dateKey);
      const finished = loadResult(config.dateKey);
      setDailyResult(finished);
      setStreak(loadStreak());

      if (saved && saved.board.length === dp.size) {
        setBoard(saved.board.map((row) => row.slice()));
        setMoves(saved.moves);
      } else {
        setBoard(deepCopy(dp.given));
        setMoves(0);
      }
      setSelected(null);
      setCheckResult(null);
      setHintsUsed(0);
      setWinOpen(false);
      setHistory([]);
      dailyStartRef.current = Date.now();
    } else {
      startPuzzle(size, pack, difficulty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, size, pack.id, difficulty]);

  // Auto-save daily progress on every board change (skip once already solved).
  useEffect(() => {
    if (mode !== "daily" || !dailyConfig || dailyResult) return;
    if (board.length === 0) return;
    saveProgress({ dateKey: dailyConfig.dateKey, board, moves });
  }, [mode, dailyConfig, dailyResult, board, moves]);

  const conflicts = useMemo(() => {
    if (!puzzle) return [] as boolean[][];
    return findConflicts(board, puzzle.boxRows, puzzle.boxCols);
  }, [board, puzzle]);

  const isLocked = mode === "daily" && !!dailyResult;

  function handleCellClick(r: number, c: number) {
    if (!puzzle || puzzle.locked[r][c] || isLocked) return;
    setSelected({ r, c });
  }

  function placeSymbol(symbol: string | null) {
    if (!puzzle || !selected || isLocked) return;
    const { r, c } = selected;
    if (puzzle.locked[r][c]) return;
    // Snapshot pre-move state so Undo can revert it.
    setHistory((h) => [...h, { board: deepCopy(board), moves, hintsUsed }]);
    setBoard((prev) => {
      const next = deepCopy(prev);
      // Tapping the already-set symbol clears the cell.
      next[r][c] = next[r][c] === symbol ? null : symbol;
      return next;
    });
    setMoves((m) => m + 1);
    setCheckResult(null);
  }

  function handleHint() {
    if (!puzzle) return;
    // Daily mode forbids hints to keep results comparable.
    if (mode === "daily") return;
    // Find empty or wrong cells and reveal one at random.
    const candidates: Array<[number, number]> = [];
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (puzzle.locked[r][c]) continue;
        if (board[r][c] !== puzzle.solution[r][c]) candidates.push([r, c]);
      }
    }
    if (candidates.length === 0) return;
    const [r, c] = candidates[Math.floor(Math.random() * candidates.length)];
    // Snapshot pre-hint state so Undo can revert the placement + counter.
    setHistory((h) => [...h, { board: deepCopy(board), moves, hintsUsed }]);
    setBoard((prev) => {
      const next = deepCopy(prev);
      next[r][c] = puzzle.solution[r][c];
      return next;
    });
    setSelected({ r, c });
    setHintsUsed((h) => h + 1);
    setCheckResult(null);
  }

  function handleCheck() {
    if (!puzzle) return;
    const filled = board.every((row) => row.every((v) => v !== null));
    if (!filled) {
      setCheckResult("incomplete");
      return;
    }
    if (isSolved(board, puzzle.solution)) {
      setCheckResult("win");
      setWinOpen(true);
    } else {
      setCheckResult("wrong");
    }
  }

  function handleClearAll() {
    if (!puzzle) return;
    if (isLocked) return;
    setBoard(deepCopy(puzzle.given));
    setSelected(null);
    setCheckResult(null);
    setMoves(0);
    setHistory([]);
  }

  function handleUndo() {
    if (isLocked) return;
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setBoard(prev.board.map((row) => row.slice()));
      setMoves(prev.moves);
      setHintsUsed(prev.hintsUsed);
      setCheckResult(null);
      return h.slice(0, -1);
    });
  }

  // Auto-detect win without a Check click.
  useEffect(() => {
    if (!puzzle) return;
    const filled = board.every((row) => row.every((v) => v !== null));
    if (!filled || !isSolved(board, puzzle.solution)) return;

    setCheckResult("win");
    setWinOpen(true);

    // Daily mode: persist result + streak exactly once.
    if (mode === "daily" && dailyConfig && !dailyResult) {
      const seconds = dailyStartRef.current
        ? Math.max(0, Math.round((Date.now() - dailyStartRef.current) / 1000))
        : undefined;
      const next: DailyResult = {
        dateKey: dailyConfig.dateKey,
        moves,
        seconds,
        completedAt: new Date().toISOString(),
      };
      const updated = saveResult(next);
      setDailyResult(next);
      setStreak(updated);
    }
  }, [board, puzzle, mode, dailyConfig, dailyResult, moves]);

  async function handleShareDaily() {
    if (!dailyConfig || !dailyResult) return;
    const text = buildShareText(dailyConfig, dailyResult);
    try {
      if (typeof navigator !== "undefined" && (navigator as Navigator).share) {
        await (navigator as Navigator).share({ text });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied!", description: "Share text copied to your clipboard." });
    } catch {
      toast({ title: "Couldn't share", description: "Try copying the text manually." });
    }
  }

  if (!puzzle) {
    return (
      <GameLayout title="Chem-doku" subtitle="Loading puzzle...">
        <div className="h-40" />
      </GameLayout>
    );
  }

  const { boxRows, boxCols, locked, symbols } = puzzle;
  const activeSize: ChemDokuSize = mode === "daily" && dailyConfig ? dailyConfig.size : size;
  const activeDifficulty: Difficulty =
    mode === "daily" && dailyConfig ? dailyConfig.difficulty : difficulty;
  const activePack: ChemDokuPack =
    mode === "daily" && dailyConfig ? dailyConfig.pack : pack;
  // Larger touch targets on mobile, with comfortable desktop sizing.
  const cellSize =
    activeSize === 4
      ? "h-[4.5rem] w-[4.5rem] sm:h-16 sm:w-16 text-xl sm:text-lg"
      : "h-12 w-12 sm:h-12 sm:w-12 text-base sm:text-sm";

  return (
    <GameLayout
      title="Chem-doku"
      subtitle="Sudoku with element symbols. Each row, column, and box must contain every symbol once."
      headerActions={<GameMenu gameKey="chem-doku" onReset={handleClearAll} resetLabel="Clear" />}
    >
      {/* Bottom padding on mobile so the fixed palette doesn't cover the action row. */}
      <div className="pb-32 sm:pb-0">
      {/* Mode toggle */}
      <div className="mb-4 inline-flex p-0.5 rounded-md border border-border bg-card">
        <button
          type="button"
          onClick={() => setMode("free")}
          aria-pressed={mode === "free"}
          className={`h-7 px-3 rounded text-xs font-medium transition-colors ${
            mode === "free"
              ? "bg-primary/15 text-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Free play
        </button>
        <button
          type="button"
          onClick={() => setMode("daily")}
          aria-pressed={mode === "daily"}
          className={`h-7 px-3 rounded text-xs font-medium inline-flex items-center gap-1 transition-colors ${
            mode === "daily"
              ? "bg-primary/15 text-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <CalendarDays size={11} aria-hidden="true" />
          Puzzle of the Day
        </button>
      </div>

      {mode === "daily" && dailyConfig ? (
        <section
          aria-label="Today's puzzle"
          className="mb-5 p-4 rounded-xl border border-primary/30 bg-primary/5 flex items-start justify-between gap-3 flex-wrap"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays size={12} className="text-primary" aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                Daily - {dailyConfig.dateKey}
              </span>
              {dailyResult && (
                <Badge variant="secondary" className="text-[10px]">
                  <CheckCircle2 size={10} className="mr-0.5" aria-hidden="true" />
                  Solved
                </Badge>
              )}
            </div>
            <p className="text-sm text-foreground">
              {dailyConfig.size}x{dailyConfig.size} - {DIFFICULTY_LABEL[dailyConfig.difficulty]} -{" "}
              <span className="text-muted-foreground">{dailyConfig.pack.label}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {dailyConfig.pack.description} Hints disabled. Same puzzle for everyone today.
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border bg-card text-xs font-medium">
              <Flame
                size={12}
                className="text-orange-500 dark:text-orange-400"
                aria-hidden="true"
              />
              <span className="font-mono">{streak.current}</span>
              <span className="text-muted-foreground">streak</span>
            </div>
            {streak.best > 0 && (
              <div className="text-[10px] text-muted-foreground mt-1">Best: {streak.best}</div>
            )}
          </div>
        </section>
      ) : (
        <>
          <section
            aria-label="Puzzle settings"
            className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3"
          >
            <SettingGroup label="Grid">
              {SIZE_OPTIONS.map((s) => (
                <PillButton
                  key={s}
                  active={size === s}
                  onClick={() => setSize(s)}
                  ariaLabel={`Use ${s} by ${s} grid`}
                >
                  {s}x{s}
                </PillButton>
              ))}
            </SettingGroup>
            <SettingGroup label="Pack">
              <select
                value={pack.id}
                onChange={(e) => setPackId(e.target.value)}
                className="h-8 px-2 rounded-md border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 w-full"
                aria-label="Symbol pack"
              >
                {PACKS[size].map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </SettingGroup>
            <SettingGroup label="Difficulty">
              {DIFFICULTIES.map((d) => (
                <PillButton
                  key={d}
                  active={difficulty === d}
                  onClick={() => setDifficulty(d)}
                  ariaLabel={`${DIFFICULTY_LABEL[d]} difficulty`}
                >
                  {DIFFICULTY_LABEL[d]}
                </PillButton>
              ))}
            </SettingGroup>
          </section>

          <p className="text-xs text-muted-foreground mb-3">
            Pack: <span className="text-foreground">{pack.description}</span>
          </p>
        </>
      )}

      {/* Grid */}
      <div className="flex flex-col items-center">
        <div
          role="grid"
          aria-label="Chem-doku board"
          className="inline-block p-1 rounded-lg border border-border bg-card"
        >
          {board.map((row, r) => (
            <div key={r} role="row" className="flex">
              {row.map((value, c) => {
                const isLocked = locked[r][c];
                const isSelected = selected?.r === r && selected?.c === c;
                const isConflict = conflicts[r]?.[c];
                const sameSymbol =
                  selected &&
                  value &&
                  board[selected.r][selected.c] === value &&
                  !(selected.r === r && selected.c === c);

                // Thick borders to mark box boundaries.
                const borderRight =
                  (c + 1) % boxCols === 0 && c !== size - 1
                    ? "border-r-2 border-r-foreground/40"
                    : "border-r border-r-border";
                const borderBottom =
                  (r + 1) % boxRows === 0 && r !== size - 1
                    ? "border-b-2 border-b-foreground/40"
                    : "border-b border-b-border";
                const borderLeft = c === 0 ? "border-l border-l-border" : "";
                const borderTop = r === 0 ? "border-t border-t-border" : "";

                let bg = "bg-background";
                let textColor = "text-foreground";
                let extra = "";
                if (value) {
                  const c = symbolColor(value);
                  bg = c.bg;
                  textColor = `${c.text} font-semibold`;
                } else if (isLocked) {
                  bg = "bg-muted/40";
                }
                if (sameSymbol) extra = "ring-2 ring-inset ring-primary/50";
                if (isSelected) extra = "ring-2 ring-inset ring-primary z-10 relative";
                if (isConflict) {
                  bg = "bg-destructive/20";
                  textColor = "text-destructive font-semibold";
                }

                return (
                  <button
                    key={c}
                    role="gridcell"
                    type="button"
                    aria-label={`Row ${r + 1} column ${c + 1}${value ? `, ${value}` : ", empty"}${isLocked ? ", clue" : ""}`}
                    aria-selected={isSelected}
                    onClick={() => handleCellClick(r, c)}
                    disabled={isLocked}
                    className={`${cellSize} ${borderRight} ${borderBottom} ${borderLeft} ${borderTop} ${bg} ${textColor} ${extra} font-mono flex items-center justify-center transition-colors disabled:cursor-default focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card focus-visible:z-10 focus-visible:relative`}
                  >
                    {value ?? ""}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Symbol palette: inline on desktop, fixed-bottom sheet on mobile for thumb reach. */}
        <div
          role="toolbar"
          aria-label="Symbol palette"
          className="mt-4 w-full sm:w-auto fixed bottom-0 left-0 right-0 z-30 px-3 py-3 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 sm:static sm:px-0 sm:py-0 sm:border-0 sm:bg-transparent sm:backdrop-blur-0 flex flex-wrap items-center justify-center gap-2 sm:gap-1.5"
        >
          {symbols.map((s) => {
            const c = symbolColor(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => placeSymbol(s)}
                disabled={!selected || isLocked}
                className={`h-12 sm:h-9 px-4 sm:px-3 rounded-md border border-border ${c.bg} ${c.text} ring-1 ring-transparent ${c.hoverRing} hover:ring-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-sm font-mono font-semibold transition-all min-w-[48px] sm:min-w-[44px]`}
              >
                {s}
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleUndo}
            disabled={history.length === 0 || isLocked}
            className="h-12 sm:h-auto px-3 sm:py-2 rounded-md border border-border bg-background sm:bg-card hover:border-primary/50 hover:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm text-muted-foreground inline-flex items-center gap-1 transition-all"
            aria-label="Undo last move"
            title="Undo last move"
          >
            <Undo2 size={14} aria-hidden="true" />
            Undo
          </button>
          <button
            type="button"
            onClick={() => placeSymbol(null)}
            disabled={!selected || isLocked}
            className="h-12 sm:h-auto px-3 sm:py-2 rounded-md border border-border bg-background sm:bg-card hover:border-destructive/50 hover:bg-destructive/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm text-muted-foreground inline-flex items-center gap-1 transition-all"
            aria-label="Erase selected cell"
          >
            <Eraser size={14} aria-hidden="true" />
            Erase
          </button>
        </div>
      </div>

      {/* Action row */}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {!isLocked && (
          <Button onClick={handleCheck} className="font-medium">
            <CheckCircle2 size={14} className="mr-1.5" aria-hidden="true" />
            Check
          </Button>
        )}
        {mode === "free" && (
          <Button onClick={handleHint} variant="secondary">
            <Lightbulb size={14} className="mr-1.5" aria-hidden="true" />
            Hint
          </Button>
        )}
        {mode === "free" && (
          <Button onClick={() => startPuzzle()} variant="ghost" size="sm">
            <Boxes size={12} className="mr-1" aria-hidden="true" />
            New puzzle
          </Button>
        )}
        {!isLocked && (
          <Button onClick={handleClearAll} variant="ghost" size="sm">
            <RotateCcw size={12} className="mr-1" aria-hidden="true" />
            Clear
          </Button>
        )}
        {mode === "daily" && dailyResult && (
          <Button onClick={handleShareDaily} className="font-medium">
            <Share2 size={14} className="mr-1.5" aria-hidden="true" />
            Share result
          </Button>
        )}
      </div>

      {/* Status row */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
        <span>Moves: <span className="font-mono text-foreground">{moves}</span></span>
        {mode === "free" && (
          <span>Hints: <span className="font-mono text-foreground">{hintsUsed}</span></span>
        )}
        {checkResult === "incomplete" && (
          <Badge variant="secondary">Fill every cell first</Badge>
        )}
        {checkResult === "wrong" && (
          <Badge variant="destructive">Some cells aren't right</Badge>
        )}
        {isLocked && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Lock size={11} aria-hidden="true" />
            New puzzle drops at midnight
          </span>
        )}
      </div>

      <Dialog open={winOpen} onOpenChange={setWinOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <Trophy size={18} className="text-primary" aria-hidden="true" />
              Solved!
            </DialogTitle>
            <DialogDescription>
              You finished a {activeSize}x{activeSize}{" "}
              {DIFFICULTY_LABEL[activeDifficulty].toLowerCase()} puzzle ({activePack.label}) in{" "}
              <strong>{moves}</strong> moves
              {mode === "free" && hintsUsed > 0
                ? ` with ${hintsUsed} hint${hintsUsed === 1 ? "" : "s"}`
                : ""}
              {mode === "daily" ? "." : "."}
              {mode === "daily" && (
                <>
                  {" "}
                  Streak: <strong>{streak.current}</strong>
                  {streak.best > streak.current ? ` (best ${streak.best})` : ""}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setWinOpen(false)}>
              Close
            </Button>
            {mode === "daily" ? (
              <Button onClick={handleShareDaily}>
                <Share2 size={14} className="mr-1.5" aria-hidden="true" />
                Share
              </Button>
            ) : (
              <Button onClick={() => startPuzzle()}>New puzzle</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </GameLayout>
  );
}

function SettingGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
        {label}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function PillButton({
  active,
  onClick,
  ariaLabel,
  children,
}: {
  active: boolean;
  onClick: () => void;
  ariaLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      className={`h-8 px-3 rounded-md border text-xs font-medium transition-colors ${
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-background text-foreground hover:border-primary/40 hover:bg-accent/30"
      }`}
    >
      {children}
    </button>
  );
}